/**
 * Attack detection: "is square `target` attacked by any piece of color `by`?"
 *
 * Implemented as reverse-attack scans from the target square (rather than
 * enumerating every attacker's moves), which is what check and move-legality
 * tests need. The flying-general rule is handled separately in `kingsFacing`.
 */

import { Position } from "./position.js";
import {
  Color,
  FILES,
  PieceType,
  fileOf,
  isOnBoard,
  rankOf,
  square,
  type Square,
} from "./types.js";

const ORTHO: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

const DIAG: ReadonlyArray<readonly [number, number]> = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

// (df, dr) offsets from a horse to the squares it can reach.
const HORSE: ReadonlyArray<readonly [number, number]> = [
  [1, 2],
  [-1, 2],
  [1, -2],
  [-1, -2],
  [2, 1],
  [-2, 1],
  [2, -1],
  [-2, -1],
];

function has(pos: Position, f: number, r: number, by: Color, type: PieceType): boolean {
  if (!isOnBoard(f, r)) return false;
  const p = pos.board[square(f, r)];
  return !!p && p.color === by && p.type === type;
}

export function isSquareAttacked(pos: Position, target: Square, by: Color): boolean {
  const tf = fileOf(target);
  const tr = rankOf(target);

  // Rook and cannon share the four orthogonal rays.
  for (const [df, dr] of ORTHO) {
    let f = tf + df;
    let r = tr + dr;
    // First piece along the ray.
    while (isOnBoard(f, r) && !pos.board[square(f, r)]) {
      f += df;
      r += dr;
    }
    if (!isOnBoard(f, r)) continue;
    const first = pos.board[square(f, r)]!;
    // A rook attacks the first piece it meets.
    if (first.color === by && first.type === PieceType.Rook) return true;
    // A cannon needs exactly one screen, then captures the next piece along the ray.
    f += df;
    r += dr;
    while (isOnBoard(f, r) && !pos.board[square(f, r)]) {
      f += df;
      r += dr;
    }
    if (!isOnBoard(f, r)) continue;
    const second = pos.board[square(f, r)]!;
    if (second.color === by && second.type === PieceType.Cannon) return true;
  }

  // Horse, respecting the blocked leg (蹩馬腿).
  for (const [df, dr] of HORSE) {
    const hf = tf - df;
    const hr = tr - dr;
    if (!has(pos, hf, hr, by, PieceType.Horse)) continue;
    const legf = hf + (Math.abs(df) === 2 ? Math.sign(df) : 0);
    const legr = hr + (Math.abs(dr) === 2 ? Math.sign(dr) : 0);
    if (!pos.board[square(legf, legr)]) return true; // leg clear
  }

  // Advisor: diagonal neighbor within the palace (advisors only ever sit in-palace).
  for (const [df, dr] of DIAG) {
    if (has(pos, tf - df, tr - dr, by, PieceType.Advisor)) return true;
  }

  // Elephant: two-step diagonal with a clear midpoint (塞象眼).
  for (const [df, dr] of DIAG) {
    const ef = tf - df * 2;
    const er = tr - dr * 2;
    if (!has(pos, ef, er, by, PieceType.Elephant)) continue;
    if (!pos.board[square(ef + df, er + dr)]) return true; // midpoint clear
  }

  // King: orthogonal neighbor (kings can never actually reach an enemy king this way,
  // but this keeps the attack model complete).
  for (const [df, dr] of ORTHO) {
    if (has(pos, tf - df, tr - dr, by, PieceType.King)) return true;
  }

  // Pawn: attacks the square straight ahead, and sideways once it has crossed the river.
  // A pawn of color `by` advances toward +rank if Red, -rank if Black.
  const fwd = by === Color.Red ? 1 : -1;
  // Pawn directly behind the target (relative to its own advance) attacks forward.
  if (has(pos, tf, tr - fwd, by, PieceType.Pawn)) return true;
  // Pawn to the left/right on the same rank attacks sideways, only if it has crossed.
  for (const df of [-1, 1]) {
    const pf = tf - df;
    const pr = tr;
    if (has(pos, pf, pr, by, PieceType.Pawn)) {
      const crossed = by === Color.Red ? pr >= 5 : pr <= 4;
      if (crossed) return true;
    }
  }

  return false;
}

/**
 * The flying-general rule (白臉將): the two kings may not stand on the same file
 * with no piece between them. Returns true if they currently do.
 */
export function kingsFacing(pos: Position): boolean {
  const rk = pos.kingSquare(Color.Red);
  const bk = pos.kingSquare(Color.Black);
  if (rk < 0 || bk < 0) return false;
  if (fileOf(rk) !== fileOf(bk)) return false;
  const file = fileOf(rk);
  const lo = Math.min(rankOf(rk), rankOf(bk));
  const hi = Math.max(rankOf(rk), rankOf(bk));
  for (let r = lo + 1; r < hi; r++) {
    if (pos.board[r * FILES + file]) return false; // something blocks the line
  }
  return true;
}

/** Is the given color's king currently in check? */
export function inCheck(pos: Position, color: Color): boolean {
  const ksq = pos.kingSquare(color);
  if (ksq < 0) return false;
  return isSquareAttacked(pos, ksq, color === Color.Red ? Color.Black : Color.Red);
}
